/* ==========================================================================
   wss.js — the two things the motion layer cannot do in CSS
   --------------------------------------------------------------------------
   1. Scroll direction, for the header that steps aside (wss.css PART 4);
      hover for the desktop menu; a steady pace for the brand ticker.
   2. Naming the clicked product card's image, so the cross-document view
      transition can carry it to the product page's main image.
   3. Showing the menu items added for new product types, once the site has
      products of that type.

   All are enhancements: without this file the header simply stays put,
   pages cross-fade without the travelling image and the menu keeps only
   its own items.
   ========================================================================== */
(() => {
  /* --- 1. Header steps aside on the way down, returns on the way up ------- */
  const START = 240;      // never hide near the top of the page
  const DEADBAND = 8;     // ignore jitter and momentum wobble
  let last = window.scrollY;
  let queued = false;

  window.addEventListener('scroll', () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      const y = window.scrollY;
      const delta = y - last;
      if (Math.abs(delta) < DEADBAND) return;
      document.body.classList.toggle('header-up', delta > 0 && y > START);
      last = y;
    });
  }, { passive: true });

  /* --- 1b. Desktop menu: opens on hover, grows down and folds back up ---
     The panel is drawn only while its <details> carries .is-shown, so the
     grow and the fold are transitions on a class (wss.css). An [open]
     attribute alone cannot do that: the browser keeps a closed <details>'
     contents alive, so a keyframe replays only on the first opening, and
     closing hides them at once. [open] is still set first and cleared only
     after the fold, so the theme's outside-click and Escape keep working.
     mouseenter/mouseleave respect DOM containment (the panel is inside its
     <details>), and a short grace covers a diagonal move across a label.
     Moving from one label to the next swaps the panel without regrowing. */
  if (window.matchMedia('(hover: hover) and (min-width: 778px)').matches) {
    const items = [...document.querySelectorAll('.main-header header .menulink details.dm.lv1')];
    const FOLD = 420;       // the fold in wss.css
    const GRACE = 160;
    let leaveTimer = 0;
    document.documentElement.classList.add('wss-mega');

    const mark = (item) => requestAnimationFrame(() => requestAnimationFrame(() => {
      if (item.open) item.classList.add('is-shown');
    }));
    const hide = (item, now) => {
      clearTimeout(item.wssFold);
      item.classList.remove('is-shown', 'is-swap');
      if (!item.open) return;
      if (now) { item.open = false; return; }
      item.wssFold = setTimeout(() => { if (!item.classList.contains('is-shown')) item.open = false; }, FOLD);
    };
    const show = (item) => {
      clearTimeout(leaveTimer);
      clearTimeout(item.wssFold);
      const swap = items.some((other) => other !== item && other.classList.contains('is-shown'));
      items.forEach((other) => { if (other !== item) hide(other, true); });
      item.classList.toggle('is-swap', swap);
      item.open = true;
      mark(item);
    };

    items.forEach((item) => {
      item.addEventListener('mouseenter', () => show(item));
      item.addEventListener('mouseleave', () => { leaveTimer = setTimeout(() => hide(item), GRACE); });
      /* A click on the label (or Enter on it) folds or opens the same way */
      item.querySelector(':scope > summary')?.addEventListener('click', (e) => {
        e.preventDefault();
        if (item.classList.contains('is-shown')) hide(item); else show(item);
      });
      /* Opened or closed by someone else (the theme's outside-click, Escape) */
      item.addEventListener('toggle', () => {
        if (!item.open) item.classList.remove('is-shown', 'is-swap');
        else if (!item.classList.contains('is-shown')) mark(item);
      });
    });
  }

  /* --- 1c. Brand ticker at a steady pace ----------------------------------
     The theme times its marquee per repeat, so a longer text runs faster.
     The list ticker (gs-marquee with "·") moves at a fixed speed instead,
     the prototype's 32px a second; design.css reads --tickerDuration. */
  const TICKER_PACE = 32;
  if (window.ResizeObserver) {
    document.querySelectorAll('.marquee--list bullet-marquee').forEach((marquee) => {
      const repeat = marquee.querySelector('.projectile');
      if (!repeat) return;
      new ResizeObserver(() => {
        marquee.style.setProperty('--tickerDuration', `${(repeat.offsetWidth / TICKER_PACE).toFixed(2)}s`);
      }).observe(repeat);
    });
  }

  /* --- 1d. Cart drawer ----------------------------------------------------
     Escape closes it; focus goes to its close button when it opens and back
     to where it was when it closes; the USDT figure under the total follows
     the cart (CartJS re-renders the hryvnia total, not the custom element). */
  const drawer = document.querySelector('.cart-drawer');
  if (drawer) {
    let before = null;
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && document.body.classList.contains('open-cart')) document.body.classList.remove('open-cart', 'hideheader');
    });
    new MutationObserver(() => {
      const open = document.body.classList.contains('open-cart');
      if (open === drawer.wssOpen) return;
      drawer.wssOpen = open;
      if (open) { before = document.activeElement; setTimeout(() => drawer.querySelector('.cartd__close')?.focus({ preventScroll: true }), 80); }
      else if (before && document.contains(before)) before.focus({ preventScroll: true });
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

    const followTotal = (cart) => {
      const usdt = drawer.querySelector('[data-cart-usdt] usdt-price');
      if (!usdt || !cart || cart.total_price == null) return;
      usdt.dataset.amount = cart.total_price;
      if (typeof usdt.applyRate === 'function') usdt.applyRate(usdt.liveRate || Number(usdt.dataset.rate));
    };
    if (window.jQuery) window.jQuery(document).on('cart.requestComplete', (event, cart) => followTotal(cart));
  }

  /* --- 2. The travelling product image ------------------------------------
     view-transition-name must be unique on a page, so only the clicked card
     gets it, and a product page gives up its own main-image name first
     (its recommendation cards lead to another product). The name stays on
     the card: coming back through the back/forward cache, the same picture
     travels home again. */
  const NAME = 'pdp-media';
  if (!('startViewTransition' in document)) return;

  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const link = e.target.closest('.cards .card-image a, .cards .card-title a');
    if (!link) return;
    const card = link.closest('x-cell');
    const img = card && card.querySelector('.card-image .primary img');
    if (!img) return;

    document.querySelectorAll('.cards .card-image img').forEach((i) => { i.style.viewTransitionName = ''; });
    document.querySelectorAll('.product-page .slider__slide:first-child img').forEach((i) => { i.style.viewTransitionName = 'none'; });
    img.style.viewTransitionName = NAME;
  }, true);

  /* Restored from the back/forward cache: the product page's own image gets
     its name back (it was set to none on the way out). */
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    document.querySelectorAll('.product-page .slider__slide:first-child img').forEach((i) => { i.style.viewTransitionName = ''; });
  });
})();

/* --- 3. Menu items for new product types -------------------------------
   snippets/menu-auto-types.liquid adds a hidden item for each product type
   of a menu section that has no item of its own. Shopify's list of types
   counts drafts too, so each section's filter is asked which types have
   products on the site (sections/menu-types.liquid) and only those items
   show. One question per section and visit (kept for half an hour). */
(() => {
  const items = [...document.querySelectorAll('li[data-menu-type][data-menu-of]')];
  if (!items.length) return;
  const KEEP = 30 * 60 * 1000;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const typesOf = async (handle) => {
    const key = `menu-types:${handle}`;
    try {
      const kept = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (kept && Date.now() - kept.at < KEEP) return kept.types;
    } catch (e) { /* private mode */ }
    const res = await fetch(`${root}collections/${encodeURIComponent(handle)}?section_id=menu-types`);
    if (!res.ok) return [];
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const types = [...doc.querySelectorAll('[data-type]')].map((i) => i.dataset.type);
    try { sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), types })); } catch (e) { /* private mode */ }
    return types;
  };

  const show = () => {
    [...new Set(items.map((li) => li.dataset.menuOf))].forEach((handle) => {
      typesOf(handle).then((types) => {
        items.forEach((li) => { if (li.dataset.menuOf === handle && types.includes(li.dataset.menuType)) li.hidden = false; });
      }).catch(() => {});
    });
  };
  if ('requestIdleCallback' in window) requestIdleCallback(show, { timeout: 2000 });
  else setTimeout(show, 600);
})();
